/**
 * This is a light-weight event emitter ported from the old app.
 */
import logger from "./debug";
export type VoidFn = () => void;
export type EventCallback = (data?: any) => void;

export interface EventCallbacks {
  [eventName: string]: EventCallback[];
}

export interface Listener {
  unsubscribe: VoidFn;
}

export interface EventEmitterOptions {
  debug?: boolean;
}

export default class EventEmitter {
  callbacks: EventCallbacks;
  debug: boolean;

  constructor({ debug }: EventEmitterOptions = {}) {
    this.callbacks = {};
    this.debug = !!debug;
  }

  /**
   * Subscribe to an event by name.
   */
  subscribe(eventName: string, callback: EventCallback): Listener {
    // Create the event name's array if not yet created
    if (!this.callbacks.hasOwnProperty(eventName)) {
      this.callbacks[eventName] = [];
    }

    // Add the listener to queue
    this.callbacks[eventName].push(callback);

    if (this.debug) {
      logger("[EventEmitter] subscribe", {
        emitter: this,
        eventName,
        callback
      });
    }

    // Provide handler for removal of event name
    const self = this;
    return {
      unsubscribe: function removeCallback() {
        if (self.debug) {
          logger("[EventEmitter] Listener.unsubscribe", {
            emitter: self,
            eventName,
            listener: this
          });
        }

        const index = self.callbacks[eventName].indexOf(callback);
        self.callbacks[eventName].splice(index, 1);

        if (!self.callbacks[eventName].length) {
          delete self.callbacks[eventName];
        }

        // NOOP the unsubscribe function to avoid any errors being thrown
        this.unsubscribe = () => {};
      }
    };
  }

  /**
   * Alias for subscribe.
   */
  on(eventName: string, callback: EventCallback): Listener {
    return this.subscribe(eventName, callback);
  }

  /**
   * Publish an event by name, along with any extra data.
   */
  publish(eventName: string, data?: any) {
    if (this.debug) {
      logger("[EventEmitter] publish", {
        emitter: this,
        eventName,
        data
      });
    }

    // If the event name doesn't exist, or there's no callbacks in queue, just leave
    if (
      !this.callbacks.hasOwnProperty(eventName) ||
      !this.callbacks[eventName].length
    ) {
      return;
    }

    // Fire each event's callbacks
    this.callbacks[eventName].forEach((callback: EventCallback) => {
      callback(data !== undefined ? data : {});
    });
  }

  /**
   * Alias for publish.
   */
  trigger(eventName: string, data?: any) {
    this.publish(eventName, data);
  }
}
